import { Suspense, lazy } from 'react';
import type { ComponentType } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Button,
  Card,
  ErrorState,
  ForfeitConfirmButton,
  LoadingState,
  PagePlate,
  StatusMessage,
} from '../../components/index.js';
import { GAME_REGISTRY } from '../../games/registry.js';
import type { GameScreenProps } from '../../games/types.js';
import { useCurrentUser } from '../../lib/auth.js';
import { describeSessionConnectionState } from '../../lib/connection-state.js';
import { getErrorMessage } from '../../lib/errors.js';
import {
  useForfeitSession,
  useGameSession,
  useSubmitGameAction,
  useCreateSoloSession,
} from '../../lib/games.js';
import { describeSessionOutcome } from '../../lib/session-outcome.js';
import { sessionControlState } from './session-controls.js';

/**
 * `React.lazy()` must be called once per component, not on every render
 * (React docs; also `eslint-plugin-react-hooks`'s `static-components`
 * rule). Since which game component to load is only known once the
 * session has loaded, this module-level cache calls `lazy()` exactly once
 * per `gameId` and reuses the same component reference afterward, from a
 * plain function outside `GameHostPage`'s render body.
 */
const lazyGameComponents = new Map<string, ComponentType<GameScreenProps>>();

function resolveGameComponent(gameId: string): ComponentType<GameScreenProps> | null {
  const cached = lazyGameComponents.get(gameId);
  if (cached) return cached;

  const loader = GAME_REGISTRY[gameId];
  if (!loader) return null;

  const Component = lazy(loader);
  lazyGameComponents.set(gameId, Component);
  return Component;
}

export interface GameSessionControlsProps {
  session: NonNullable<ReturnType<typeof useGameSession>['data']>;
  selfUserId: string | undefined;
  isForfeiting: boolean;
  isCreatingSolo: boolean;
  onForfeit: () => void;
  onPlayAgain: () => void;
}

export function GameSessionControls({
  session,
  selfUserId,
  isForfeiting,
  isCreatingSolo,
  onForfeit,
  onPlayAgain,
}: GameSessionControlsProps) {
  const controls = sessionControlState(session, selfUserId);

  if (controls.showAbandon) {
    return (
      <div className="game-session-controls flex justify-end" data-session-control="abandon">
        <ForfeitConfirmButton
          isSolo={session.mode === 'solo'}
          participantCount={session.players.length}
          isLoading={isForfeiting}
          onConfirm={onForfeit}
          triggerClassName="session-control-button session-control-button--abandon"
          triggerButtonProps={{ 'data-session-control-button': 'abandon' }}
        />
      </div>
    );
  }

  if (controls.playAgainTarget !== null) {
    return (
      <div className="game-session-controls flex justify-end" data-session-control="play-again">
        <Button
          variant="primary"
          size="sm"
          isLoading={isCreatingSolo}
          onClick={onPlayAgain}
          className="session-control-button session-control-button--play-again"
          data-session-control-button="play-again"
        >
          Play Again
        </Button>
      </div>
    );
  }

  return null;
}

/**
 * The shared `/play/:sessionId` game host (docs/APP_PLAN.md §5.2, §12 M3,
 * M6; TECH_STACK §11, §43). This is a **platform-level** route: it loads
 * the session, resolves the registered component for `session.gameId` from
 * `apps/web/src/games/registry.ts`, and renders it inside the shared
 * application shell — the same shell every other route uses (APP_SPEC
 * §33). It contains no game-specific logic: the game lookup is a generic
 * map read, and the host never inspects `session.view` or decides
 * legality, turn, completion, or score (CLAUDE.md §2, §4).
 *
 * The resign/forfeit action and the completion banner below are also
 * platform-level (docs/APP_PLAN.md §8.6): forfeit is a platform operation,
 * not a game rule, so who won is decided entirely from `session.players[
 * ].result` — never from the game's own view, which is only required to
 * annotate its state for presentation on forfeit (`onForfeit`), not to
 * encode the outcome.
 *
 * `width="wide"` is this route's real content requirement, not a style
 * choice: this route is exclusively game pages and lobbies (every game's
 * own lobby renders inside its `GameScreen`, which renders here), and those
 * need the full board/party rail/ability bar composition's width. It is
 * also, as of 2026-09-20, the reason `PageLayout`/`PagePlate` both default
 * to `wide` now — every other page's width matches this one rather than
 * each page picking its own independently, which is how the app ended up
 * with visibly different widths from one page to the next.
 *
 * The shell itself is `PagePlate`, shared with the Games and Profile plates,
 * so the plate radius / panel radius / frame corner size cannot drift apart
 * here again — this page previously declared two of those three and omitted
 * the plate radius, leaving 20px parchment corners inside a 32/40px frame.
 * Only `width` and the tighter `gap` are this route's own.
 */
export function GameHostPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const sessionQuery = useGameSession(sessionId);
  const submitAction = useSubmitGameAction(sessionQuery.data);
  const forfeitSession = useForfeitSession(sessionQuery.data);
  const createSolo = useCreateSoloSession();
  const currentUser = useCurrentUser();

  if (!sessionId) {
    return <ErrorState message="No game session was specified." />;
  }

  // `describeSessionConnectionState` (docs/games/word-rush/GAME_PLAN.md
  // §11; bug fix, see that module's doc comment): a background poll
  // failure with a board already loaded shows a non-destructive
  // "reconnecting" banner below, never a blank/error page that discards
  // the player's board — only a failure with *no* data yet to show falls
  // back to the blocking `ErrorState`.
  const connectionState = describeSessionConnectionState({
    isPending: sessionQuery.isPending,
    isError: sessionQuery.isError,
    hasData: sessionQuery.data !== undefined,
  });

  if (connectionState === 'loading') {
    return <LoadingState label="Loading game…" />;
  }

  if (connectionState === 'blocking-error') {
    return (
      <ErrorState
        message={getErrorMessage(sessionQuery.error)}
        onRetry={() => void sessionQuery.refetch()}
      />
    );
  }

  const session = sessionQuery.data;
  if (!session) {
    // Unreachable given `connectionState` above ('ok'/'reconnecting' both imply `hasData`),
    // kept only so TypeScript's control-flow analysis stays sound without an `as` cast.
    return <LoadingState label="Loading game…" />;
  }
  const GameComponent = resolveGameComponent(session.gameId);
  if (!GameComponent) {
    return <ErrorState message="This game is not available." />;
  }

  const selfId = currentUser.data?.id;
  const controls = sessionControlState(session, selfId);
  const sessionForPlayAgain = session;
  // Non-`normal` completions (forfeit, block-triggered forfeit, or solo
  // abandonment) get an explicit platform banner here because the game's
  // own "complete" view is not guaranteed to encode who won a forfeit
  // (`onForfeit` may only annotate its state for presentation, never the
  // outcome itself, docs/APP_PLAN.md §8.6) — a normal completion is left
  // to the game's own view, which already renders it correctly.
  const outcome =
    session.status === 'completed' &&
    session.completionReason !== null &&
    session.completionReason !== 'normal' &&
    selfId
      ? describeSessionOutcome(session, selfId)
      : null;

  function playAgain() {
    if (controls.playAgainTarget === 'solo') {
      // For solo games, create a new solo session and navigate to it
      createSolo.mutate(sessionForPlayAgain.gameId, {
        onSuccess: (newSession) => {
          void navigate(`/play/${newSession.id}`);
        },
      });
    } else if (controls.playAgainTarget === 'multiplayer-catalog') {
      void navigate(
        `/games?create=multiplayer&game=${encodeURIComponent(sessionForPlayAgain.gameId)}`,
      );
    }
  }

  return (
    <PagePlate
      width="wide"
      gap="compact"
      className="game-session-host"
      data-game-id={session.gameId}
    >
      {connectionState === 'reconnecting' && (
        <StatusMessage
          tone="warning"
          title="Reconnecting…"
          data-testid="session-reconnecting-banner"
        >
          We couldn&rsquo;t reach the server for the latest update. Retrying automatically — your
          game is safe.
        </StatusMessage>
      )}

      {forfeitSession.isError && <ErrorState message={getErrorMessage(forfeitSession.error)} />}

      {outcome && (
        <Card data-testid="session-outcome-banner" variant="framed">
          <h2 className="font-display text-lg font-semibold text-textPrimary">
            {outcome.headline}
          </h2>
          {outcome.detail && <p className="mt-1 text-sm text-textSecondary">{outcome.detail}</p>}
        </Card>
      )}

      <div className="game-content-frame">
        <GameSessionControls
          session={session}
          selfUserId={selfId}
          isForfeiting={forfeitSession.isPending}
          isCreatingSolo={createSolo.isPending}
          onForfeit={() => forfeitSession.mutate()}
          onPlayAgain={playAgain}
        />

        <Suspense fallback={<LoadingState label="Loading game…" />}>
          {/* eslint-disable-next-line react-hooks/static-components -- this IS the documented dynamic
                plugin-component pattern (docs/APP_PLAN.md §5.2, TECH_STACK §11, §43): which component
                renders here is only known at runtime from `session.gameId`, resolved through the
                per-gameId-cached `resolveGameComponent` above rather than re-created every render. */}
          <GameComponent
            session={session}
            onSubmitAction={(action) => submitAction.mutate(action)}
            isSubmitting={submitAction.isPending}
            actionError={submitAction.isError ? getErrorMessage(submitAction.error) : null}
          />
        </Suspense>
      </div>
    </PagePlate>
  );
}
